import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { queryOptions, useMutation, useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { CustomerForm } from '@apple/features/customer/components/CustomerManagement/CustomerForm';
import {
	deleteCustomer,
	getSingleCustomer,
	saveCustomer,
} from '@apple/features/customer/management';
import { programsQueryOptions } from '@apple/features/program/queries';

export const Route = createFileRoute('/_authed/_admin/manage/customers_/$customerCode')({
	parseParams: ({ customerCode }) => ({
		customerCode: z.string().parse(customerCode),
	}),
	beforeLoad: ({ context, location, params: { customerCode }, cause }) => {
		requireAuth(
			{ context, location, cause },
			{
				requirePermission: 'Manzanita.Security.Features.CustomerManagement',
			},
		);

		return {
			customerQueryOptions: queryOptions({
				queryKey: ['customer', customerCode],
				queryFn: () => getSingleCustomer(customerCode),
				staleTime: 5 * 60 * 1000, // 5 minutes
			}),
			programsQueryOptions: programsQueryOptions(),
		};
	},
	loader: ({ context: { queryClient, customerQueryOptions, programsQueryOptions } }) => {
		void queryClient.prefetchQuery(customerQueryOptions);
		void queryClient.prefetchQuery(programsQueryOptions);
	},
	wrapInSuspense: true,
	component: CustomerRoute,
});

function CustomerRoute() {
	const { t } = useTranslation('customer');
	const { customerQueryOptions, programsQueryOptions, queryClient } = Route.useRouteContext();

	const [customerQuery, programsQuery] = useSuspenseQueries({
		queries: [customerQueryOptions, programsQueryOptions],
	});

	const goBackToList = useBackOrNavigate();

	const updateCustomer = useMutation({
		mutationFn: saveCustomer,
		onSuccess: async () => {
			notifications.show({
				color: 'green',
				message: t('update.success'),
				icon: <Success />,
			});
			await queryClient.invalidateQueries({
				queryKey: customerQueryOptions.queryKey,
			});
			await queryClient.invalidateQueries({ queryKey: ['customers-odata'] });
			await goBackToList('/manage/customers');
		},
		onError: (error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
	});

	const deleteCustomerMutation = useMutation({
		mutationFn: deleteCustomer,
		onSuccess: async () => {
			notifications.show({
				color: 'green',
				message: t('delete.success'),
				icon: <Success />,
			});
			await queryClient.invalidateQueries({
				queryKey: ['customers-odata'],
			});
			await goBackToList('/manage/customers');
		},
		onError: () => {
			notifications.show({
				color: 'red',
				message: t('delete.error'),
				icon: <ErrorIcon />,
			});
		},
	});

	const handleDeleteCustomer = useCallback(
		(customerCode: string) => {
			modals.openConfirmModal({
				title: t('delete.title'),
				labels: {
					confirm: t('common:buttons.confirm'),
					cancel: t('common:buttons.cancel'),
				},
				onConfirm: () => deleteCustomerMutation.mutate(customerCode),
				children: <Text>{t('delete.message')}</Text>,
			});
		},
		[deleteCustomerMutation, t],
	);

	return (
		<DetailsLayout title={t('title.edit')}>
			<CustomerForm
				isEdit={true}
				onClose={() => void goBackToList('/manage/customers')}
				onSaveCustomer={updateCustomer.mutateAsync}
				customer={customerQuery.data}
				programs={programsQuery.data}
				onDeleteCustomer={handleDeleteCustomer}
			/>
		</DetailsLayout>
	);
}
